import { AfterViewInit, Component, ElementRef, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { moveItemInArray } from '@angular/cdk/drag-drop';

import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { InfluencerStreamsService } from './influencer-streams-service';
import { DiscoverRoutingService } from '../../../DiscoverRouter/services/discover-routing.service';
import { InfluencerStreamApiModel } from '../../../../api/targeting/model/influencer-stream.api.model';
import { TargetingSearchesService } from '../../services/targeting-searches.service';
import { TargetingListService } from '../../services/targeting-list.service';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { InfluencerStreamComponent, MentionSelectedEvent } from './InfluencerStream/influencer-stream.component';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { TargetingRhpDialogService } from '../../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { RhpContextsComponent } from '../../../../Common/RhpContexts/rhp-contexts.component';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { ButtonComponent } from '../../../../Common/Forms/components/Button/button.component';
import { EntityTakeoverModel } from '../../../../Common/RhpContexts/models/entity-takeover.model';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';


@Component({
    selector: 'agpr-influencer-streams',
    templateUrl: './influencer-streams.component.html',
    styleUrls: ['./influencer-streams.component.scss'],
    providers: [
        {
            provide: MentionsService,
            useExisting: InfluencerStreamsService
        }
    ],
    standalone: false
})
export class InfluencerStreamsComponent implements OnDestroy, AfterViewInit {

    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly entityType = EntityType;

    streams: InfluencerStreamApiModel[] = null;

    subscriptions: Subscription[] = [];

    sidebarExpanded;
    rhpTakeOver;

    noContentFound: boolean = false;

    focusedMention: Mention = null;

    streamContactsTabHeader: string = null;

    activeStream: number;

    @ViewChildren('influencerStream') stream: QueryList<InfluencerStreamComponent>;
    @ViewChild('rhpContexts') rhpTabs: RhpContextsComponent;
    @ViewChild('addNewStreamButton') addNewStreamButton: ButtonComponent;
    @ViewChild('dragUpdate') dragUpdatePanel: ElementRef;

    constructor(
        public discoverRoutingService: DiscoverRoutingService, // ensure it's created before this component
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public influencerStreamsService: InfluencerStreamsService,
        public targetingSearchesService: TargetingSearchesService,
        public targetingListService: TargetingListService,
        public targetingRhpDialogService: TargetingRhpDialogService
    ) {
        this.subscriptions.push(
            this.influencerStreamsService.getStreams().subscribe(streams => {
                if (!streams) {
                    this.noContentFound = true;
                    return;
                }
                this.streams = streams;
                if (this.streams.length > 0) {
                    this.streams.forEach(stream => {
                        stream.editing = false;
                        stream.loading = false;
                    });
                }
            }),
            this.influencerStreamsService.getFocusedMention().subscribe(mention => {
                this.noContentFound = !mention;
                this.focusedMention = mention;
            })
        );
    }

    ngAfterViewInit() {
        this.subscriptions.push(
            this.influencerStreamsService.focusedStreamId$.subscribe(id => {
                if (id) {
                    requestAnimationFrame(() => {
                        const focusedStreamComponent = this.stream?.toArray().find(x => x._stream.id === id);
                        let streamName: string;
                        if (focusedStreamComponent._stream.keyphrase) {
                            streamName = focusedStreamComponent._stream.keyphrase;
                        } else if (focusedStreamComponent.selectedSource) {
                            streamName = focusedStreamComponent.selectedSource.name;
                        }
                        this.streamContactsTabHeader = this.localizationService.transform(
                            'Discover.influencerStreams.streamContactsHeader', {name: streamName});
                    });

                }
            })
        );
    }
    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    drop(event) {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(this.streams, event.previousIndex, event.currentIndex);
            this.influencerStreamsService.setStreamOrder();
        }
    }

    unsetActiveStream(index) {
        if (this.activeStream === index) {
            this.activeStream = null;
        }
    }

    setActiveStream(index) {
        this.activeStream = index;
    }

    onKeydown(event: KeyboardEvent, index: number) {
        if (event.key !== ActionKeys.RIGHT && event.key !== ActionKeys.LEFT) {
            return;
        }
        let newIndex = index;
        event.preventDefault();
        if (event.key === ActionKeys.RIGHT) {
            newIndex++;
        }
        if (event.key === ActionKeys.LEFT) {
            newIndex--;
        }
        if (newIndex !== index && newIndex < this.streams.length && newIndex >= 0) {
            this.activeStream = newIndex;
            moveItemInArray(this.streams, index, newIndex);
            this.influencerStreamsService.setStreamOrder();
            requestAnimationFrame(() => (event.target as HTMLElement).focus());
            this.addDragUpdateMessage(newIndex);
        }
    }

    addDragUpdateMessage(index: number) {
        const update = document.createElement('DIV');
        update.textContent = this.localizationService.transform('Discover.influencerStreams.dragUpdate', {index: index + 1, length: this.streams.length});
        (this.dragUpdatePanel.nativeElement as HTMLElement).appendChild(update);

        setTimeout(() => {
            (this.dragUpdatePanel.nativeElement as HTMLElement).removeChild(update);
        }, 300);
    }

    toggleRhpTakeover(arg) {

    }

    createStream() {
        this.streams.push({});
    }

    /**
     *  Make the decision to focus which stream when a stream is deleted
     */
    streamDeleted(deletedIndex: number, nextIndex = deletedIndex) {
        nextIndex = (nextIndex + 1) % this.streams.length;
        if (nextIndex !== deletedIndex) {
            this.stream.toArray()[nextIndex].focusFirstMention();
            if (!this.focusedMention) {
                this.streamDeleted(deletedIndex, nextIndex);
            }
        }
    }

    spawnTakeover(event: EntityTakeoverModel) {
        this.targetingRhpDialogService.openTakeOver({
            inputs: {
                contactId: event.contactId,
                outletId: event.outletId,
                entityType: event.entityType,
                isPrivate: event.isPrivate
            }
        });
    }

    streamMentionSelected(event: MentionSelectedEvent) {
        const contactUuids = event.visibleStreamMentions.map(mention => {
            return mention.author.uuid;
        });
        this.influencerStreamsService.setFocusedMention(event.mention, event.openOutletTab, contactUuids , event.streamId);
    }

    skipCurrentStream(currentFocusedStreamIndex: number) {
        // Move focus to the first focusable of the next stream if there is any
        const nextFocusedStream =  this.stream.toArray()[currentFocusedStreamIndex + 1];
        if (nextFocusedStream) {
            nextFocusedStream.focusableElements[0]?.focus();
        } else {
            const addNewStreamButtonEl =  this.addNewStreamButton.element.nativeElement?.querySelector('button');
            addNewStreamButtonEl?.focus();
        }
    }
}
